// src/pages/TimeSeries/components/TimeSeriesGroupsManager.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Row,
  Col,
  Input,
  Badge,
} from "reactstrap";
import { autoLabelGroups, getAvailableFiles, getGroupFiles } from "../../../utils/groupUtils";

// Reusable File Item Component
const FileItem = React.memo(
  ({
    file,
    fileId,
    isDragging,
    onDragStart,
    onDragEnd,
    onRemove,
    timepointIndex,
    seqType,
    condType,
  }) => (
    <div
      key={fileId}
      draggable={true}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
        padding: "3px 12px",
        backgroundColor: isDragging
          ? "rgba(255,255,255,0.5)"
          : "rgba(255,255,255,0.8)",
        borderRadius: "6px",
        fontSize: "11px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        border: "1px solid rgba(255,255,255,0.5)",
        cursor: "grab",
        transition: "all 0.2s ease",
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? "scale(0.95)" : "scale(1)",
        marginBottom: "4px",
      }}
      onMouseEnter={(e) => {
        if (!isDragging) {
          e.currentTarget.style.cursor = "grabbing";
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          e.currentTarget.style.cursor = "grab";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      <span
        style={{
          overflow: "hidden",
          color: "#374151",
          fontWeight: "500",
          flex: 1,
          marginRight: "8px",
        }}
        title={file?.file_name || fileId}
      >
        {file?.file_name || fileId}
      </span>
      <Button
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(timepointIndex, seqType, condType, [fileId]);
        }}
        style={{
          backgroundColor: "transparent",
          border: "none",
          color: "#dc3545",
          padding: "2px 4px",
          fontSize: "10px",
          minWidth: "20px",
          height: "20px",
        }}
      >
        <i className="ri-close-line"></i>
      </Button>
    </div>
  )
);

// Reusable Timepoint Card Component
const TimepointCard = React.memo(
  ({
    seqType,
    condType,
    files,
    timepointIndex,
    dragOverTarget,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onClearTimepoint, // This should clear only the specific section
    renderFileItem,
    getBorderColor,
    getBackgroundColor,
  }) => {
    const isActive =
      dragOverTarget === `${timepointIndex}-${seqType}-${condType}`;

    // Handle clearing this specific timepoint section
    const handleClearSection = useCallback(() => {
      // Only clear files from this specific section (timepointIndex + seqType + condType)
      onClearTimepoint(timepointIndex, seqType, condType);
    }, [onClearTimepoint, timepointIndex, seqType, condType]);

    return (
      <Card
        style={{
          backgroundColor: getBackgroundColor(seqType),
          border: isActive
            ? `2px dashed ${getBorderColor(seqType)}`
            : `1px solid ${getBorderColor(seqType)}`,
          borderRadius: "8px",
          transition: "all 0.2s ease",
          transform: isActive ? "scale(1.02)" : "scale(1)",
          boxShadow: isActive
            ? `0 4px 12px rgba(0,0,0,0.15), 0 0 0 4px ${getBorderColor(
                seqType
              )}20`
            : "none",
          marginBottom: "16px",
        }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <CardBody style={{ padding: "16px" }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  backgroundColor: getBorderColor(seqType),
                  marginRight: "8px",
                }}
              />
              <span
                style={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: getBorderColor(seqType),
                }}
              >
                {seqType === "ribo" ? "Ribo-Seq" : "RNA-Seq"}
              </span>
            </div>
            {/* Clear Group Button */}
            <Button
              size="sm"
              onClick={() => onClearTimepoint?.(timepointIndex, seqType, condType)}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #f59e0b",
                color: "#f59e0b",
                fontSize: "10px",
                padding: "4px 8px",
                fontWeight: "500",
                borderRadius: "4px",
                marginTop: "8px",
              }}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Clear
            </Button>
          </div>

          {/* Files or Drop Zone */}
          {files.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100px",
                border: "2px dashed #ccc",
                borderRadius: "8px",
                backgroundColor: "rgba(255,255,255,0.5)",
                color: "#999",
                fontSize: "11px",
                textAlign: "center",
              }}
            >
              <i
                className="ri-upload-cloud-2-line"
                style={{
                  fontSize: "20px",
                  marginBottom: "6px",
                }}
              />
              <div>Drag files here</div>
            </div>
          ) : (
            <div
              style={{
                overflowY: "auto",
                paddingRight: "4px",
                minHeight: "100px"
              }}
            >
              {files.map((fileId) =>
                renderFileItem(fileId, condType, timepointIndex, seqType)
              )}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }
);

const TimeSeriesGroupsManager = ({
  files = [],
  allFiles = [],
  timepoints = [],
  settings = {},
  onUpdateSetting,
  onAddTimepoint,
  onRemoveTimepoint,
  onUpdateTimepointName,
  onAddFilesToTimepoint,
  onRemoveFilesFromTimepoint,
  onClearTimepoint, // This should be called with (timepointIndex, seqType, condType)
  onClearAll,
  currentMaxVisible = 0,
  maxTimepoints = 12,
}) => {
  const [draggedFiles, setDraggedFiles] = useState([]);
  const [draggedFileSourceGroup, setDraggedFileSourceGroup] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);

  // Create displayGroups used for the quick assign bar buttons
  const groupConfigs = [
    { seqType: "ribo", condType: "control", color: "#E8F5E8" },
    { seqType: "rna",  condType: "control", color: "#FDEDEE" },
    { seqType: "ribo", condType: "treat",   color: "#E8F5E8" },
    { seqType: "rna",  condType: "treat",   color: "#FDEDEE" },
  ];

  const displayGroups = Array.from({ length: maxTimepoints + 1 }).flatMap((_, i) => 
    groupConfigs.map(config => ({ id: `${config.condType}-${config.seqType}-${i}`,
                                  timepointIndex: i, 
                                  ...config }))
  );
 
  // Get available files (not assigned to any timepoint)
  const getAvailableFiles = useMemo(() => {
    return files.filter(
      (file) =>
        !timepoints.some(
          (tp) =>
            tp.controlRiboFiles.includes(file.file_id) ||
            tp.controlRnaFiles.includes(file.file_id) ||
            tp.treatRiboFiles.includes(file.file_id) ||
            tp.treatRnaFiles.includes(file.file_id)
        )
    );
  }, [files, timepoints]);

  const minCellWidth = useMemo(() => {
    if (getAvailableFiles.length === 0) return 180;
      const longestName = Math.max(...getAvailableFiles.map(f => f.file_name.length));
      const calculated = longestName * 7 + 40;
    return Math.min(Math.max(calculated, 180), 280);
  }, [getAvailableFiles]);
    useEffect(() => {
      if (Array.isArray(allFiles) && allFiles.length > 0) {
        window.globalFileCache = allFiles;
      }
  }, [allFiles]);

  const getFileById = useCallback(
    (fileId) => allFiles.find((f) => f.file_id === fileId),
    [allFiles]
  );

  // Mouse handlers for consistent styling
  const handleFileMouseEnter = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "#f9fafb";
      e.currentTarget.style.borderColor = "#d1d5db";
    }
  }, []);

  const handleFileMouseLeave = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "white";
      e.currentTarget.style.borderColor = "#e5e7eb";
    }
  }, []);

  // Enhanced drag handlers
  const handleDragStart = useCallback(
    (e, fileId) => {
      try {
        const filesToDrag = selectedFiles.includes(fileId)
          ? selectedFiles
          : [fileId];
        setDraggedFiles(filesToDrag);
        setDraggedFileSourceGroup(null);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", fileId);
        e.dataTransfer.setData("application/json", JSON.stringify(filesToDrag));
        document.body.style.cursor = "grabbing";
      } catch (error) {
        console.error("Drag start error:", error);
      }
    },
    [selectedFiles]
  );

  const handleGroupFileDragStart = useCallback(
    (e, fileId, sourceTimepointIndex, sourceSeqType, sourceCondType) => {
      e.stopPropagation();
      setDraggedFiles([fileId]);
      setDraggedFileSourceGroup({
        timepointIndex: sourceTimepointIndex,
        seqType: sourceSeqType,
        condType: sourceCondType,
      });
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", fileId);
      e.dataTransfer.setData("application/json", JSON.stringify([fileId]));
      document.body.style.cursor = "grabbing";
    },
    []
  );

  const handleDragEnter = useCallback(
    (e, timepointIndex, seqType, condType) => {
      e.preventDefault();
      setDragOverTarget(`${timepointIndex}-${seqType}-${condType}`);
    },
    []
  );

  const handleDragOver = useCallback((e, timepointIndex, seqType, condType) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTarget(`${timepointIndex}-${seqType}-${condType}`);
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTarget(null);
    }
  }, []);

  const handleDrop = useCallback(
    (e, timepointIndex, seqType, condType) => {
      e.preventDefault();
      e.stopPropagation();

      let filesToAdd = [];
      try {
        const jsonData = e.dataTransfer.getData("application/json");
        if (jsonData) {
          filesToAdd = JSON.parse(jsonData);
        } else {
          const singleFile = e.dataTransfer.getData("text/plain");
          if (singleFile) filesToAdd = [singleFile];
        }
      } catch (error) {
        console.warn(
          "Failed to get drag data from dataTransfer, using fallback:",
          error
        );
        if (draggedFiles.length > 0) {
          filesToAdd = draggedFiles;
        }
      }

      if (filesToAdd.length > 0) {
        if (draggedFileSourceGroup) {
          const {
            timepointIndex: sourceTimepointIndex,
            seqType: sourceSeqType,
            condType: sourceCondType,
          } = draggedFileSourceGroup;

          const isDifferentLocation =
            sourceTimepointIndex !== timepointIndex ||
            sourceSeqType !== seqType ||
            sourceCondType !== condType;

          if (isDifferentLocation) {
            onRemoveFilesFromTimepoint(
              sourceTimepointIndex,
              sourceSeqType,
              sourceCondType,
              filesToAdd
            );
            onAddFilesToTimepoint(
              timepointIndex,
              seqType,
              condType,
              filesToAdd
            );
          }
        } else {
          onAddFilesToTimepoint(timepointIndex, seqType, condType, filesToAdd);
          setSelectedFiles([]);
        }

        setDraggedFiles([]);
        setDraggedFileSourceGroup(null);
      }

      setDragOverTarget(null);
      document.body.style.cursor = "default";
    },
    [
      draggedFiles,
      draggedFileSourceGroup,
      onAddFilesToTimepoint,
      onRemoveFilesFromTimepoint,
    ]
  );

  const handleDragEnd = useCallback(() => {
    setDragOverTarget(null);
    setDraggedFiles([]);
    setDraggedFileSourceGroup(null);
    document.body.style.cursor = "default";
  }, []);

  const handleFileClick = useCallback(
    (e, fileId, fileIndex) => {
      e.preventDefault();

      const isAssigned = timepoints.some(
        (tp) =>
          tp.controlRiboFiles.includes(fileId) ||
          tp.controlRnaFiles.includes(fileId) ||
          tp.treatRiboFiles.includes(fileId) ||
          tp.treatRnaFiles.includes(fileId)
      );
      if (isAssigned) return;

      if (e.shiftKey && lastSelectedIndex !== -1) {
        const start = Math.min(lastSelectedIndex, fileIndex);
        const end = Math.max(lastSelectedIndex, fileIndex);
        const rangeFiles = getAvailableFiles
          .slice(start, end + 1)
          .map((file) => file.file_id);
        setSelectedFiles((prev) => [...new Set([...prev, ...rangeFiles])]);
      } else {
        setSelectedFiles((prev) =>
          prev.includes(fileId)
            ? prev.filter((id) => id !== fileId)
            : [...prev, fileId]
        );
        setLastSelectedIndex(fileIndex);
      }
    },
    [timepoints, lastSelectedIndex, getAvailableFiles]
  );

  // Determine which sequence types to show
  const shouldShowSeqType = useCallback(
    (seqType) => {
      const selectedSeqType = settings.selectedSeqType;
      return selectedSeqType === "TE" || selectedSeqType === seqType;
    },
    [settings.selectedSeqType]
  );

  const shouldShowTreatment = useCallback(() => {
    return settings.analysisType !== "single_control";
  }, [settings.analysisType]);

  const getBackgroundColor = useCallback((seqType) => {
    return seqType === "ribo" ? "#E8F5E8" : "#FDEDEE";
  }, []);

  const getBorderColor = useCallback((seqType) => {
    return seqType === "ribo" ? "#1a6d51" : "#dc3545";
  }, []);

  // Create a wrapper for onClearTimepoint that ensures correct parameter passing
  const handleClearTimepointSection = useCallback(
    (timepointIndex, seqType, condType) => {
      console.log(
        `Clearing section: timepoint=${timepointIndex}, seqType=${seqType}, condType=${condType}`
      );
      // This should only clear the specific section, not the entire timepoint
      onClearTimepoint(timepointIndex, seqType, condType);
    },
    [onClearTimepoint]
  );

  // Reusable file item renderer
  const renderFileItem = useCallback(
    (fileId, condType, timepointIndex, seqType) => {
      const file = getFileById(fileId);
      const isDragging = draggedFiles.includes(fileId);

      return (
        <FileItem
          key={fileId}
          file={file}
          fileId={fileId}
          isDragging={isDragging}
          onDragStart={(e) =>
            handleGroupFileDragStart(
              e,
              fileId,
              timepointIndex,
              seqType,
              condType
            )
          }
          onDragEnd={handleDragEnd}
          onRemove={onRemoveFilesFromTimepoint}
          timepointIndex={timepointIndex}
          seqType={seqType}
          condType={condType}
        />
      );
    },
    [
      getFileById,
      onRemoveFilesFromTimepoint,
      draggedFiles,
      handleGroupFileDragStart,
      handleDragEnd,
    ]
  );

  const availableFilesCount = getAvailableFiles.length;
  const isSingleControl = settings.analysisType === "single_control";

  return (
    <Card
      className="mb-4"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "1px",
        height: "100%",
        minHeight: "400px",
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div>
            <h5
              className="mb-0"
              style={{ color: "#1a6d51", fontSize: "18px", fontWeight: "600" }}
            >
              <i className="ri-time-line me-2"></i>
              Time Series File Assignment
            </h5>
          </div>

          <div className="d-flex gap-2">
            <Button
              size="sm"
              onClick={onClearAll}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #dc3545",
                color: "#dc3545",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Clear All
            </Button>
            <Button
              size="sm"
              onClick={onAddTimepoint}
              disabled={currentMaxVisible >= maxTimepoints}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-add-line me-1"></i>
              Add Timepoint
            </Button>
            <Button
              size="sm"
              onClick={onRemoveTimepoint}
              disabled={currentMaxVisible <= 0}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-subtract-line me-1"></i>
              Remove Timepoint
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardBody style={{ padding: "20px" }}>
        {/* Available Files Section */}
        <div className="mb-4">
          {availableFilesCount > 0 && (
            <div
              className="text-center mb-2"
              style={{
                background:
                  "linear-gradient(135deg, #1a6d51 0%, #29a161ff 100%)",
                color: "white",
                borderRadius: "6px",
                padding: "4px 12px",
                fontSize: "11px",
                display: "inline-block",
              }}
            >
              <i
                className="ri-cursor-line me-1"
                style={{ fontSize: "12px" }}
              ></i>
              Click files to select/deselect them. Use{" "}
              <kbd
                style={{
                  fontSize: "9px",
                  padding: "1px 3px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                }}
              >
                Shift
              </kbd>
              +Click for range selection.
            </div>
          )}

          <div
            style={{
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              padding: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${minCellWidth}px, 1fr))`,
                gap: "6px",
              }}
            >
              {getAvailableFiles.map((file, index) => {
                const isSelected = selectedFiles.includes(file.file_id);
                const isAssigned = timepoints.some(
                  (tp) =>
                    tp.controlRiboFiles.includes(file.file_id) ||
                    tp.controlRnaFiles.includes(file.file_id) ||
                    tp.treatRiboFiles.includes(file.file_id) ||
                    tp.treatRnaFiles.includes(file.file_id)
                );

                return (
                  <div
                    key={file.file_id}
                    draggable={!isAssigned}
                    onDragStart={(e) =>
                      !isAssigned && handleDragStart(e, file.file_id)
                    }
                    onDragEnd={handleDragEnd}
                    onClick={(e) => handleFileClick(e, file.file_id, index)}
                    onMouseEnter={(e) =>
                      handleFileMouseEnter(e, isAssigned, isSelected)
                    }
                    onMouseLeave={(e) =>
                      handleFileMouseLeave(e, isAssigned, isSelected)
                    }
                    style={{
                      padding: "6px",
                      border: isSelected
                        ? "1px solid #2F9E37"
                        : "1px solid #e5e7eb",
                      borderRadius: "4px",
                      backgroundColor: isAssigned
                        ? "#f3f4f6"
                        : isSelected
                        ? "#f0fdf4"
                        : "white",
                      cursor: isAssigned ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      opacity: isAssigned ? 0.7 : 1,
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      userSelect: "none",
                    }}
                  >
                    <Input
                      type="checkbox"
                      checked={isSelected && !isAssigned}
                      readOnly
                      style={{
                        pointerEvents: "none",
                        margin: 0,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        lineHeight: "1.2",
                        overflow: "hidden",
                        flex: 1,
                        color: isAssigned ? "#9ca3af" : "#374151",
                      }}
                      title={file.file_name}
                    >
                      {file.file_name}
                    </span>
                    {isAssigned && (
                      <Badge
                        style={{
                          fontSize: "8px",
                          backgroundColor: "#9ca3af",
                          padding: "2px 4px",
                        }}
                      >
                        Used
                      </Badge>
                    )}
                  </div>
                );
              })}
              {availableFilesCount === 0 && (
                <div
                  className="text-muted text-center"
                  style={{
                    gridColumn: "1 / -1",
                    padding: "20px",
                    fontSize: "14px",
                  }}
                >
                  All files have been assigned to timepoints
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Assign Bar */} 
        {selectedFiles.length > 0 && (
          <div className="mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-bold">
                Assign {selectedFiles.length} selected file(s) to:
              </span>
              <div className="row g-2" style={{ maxWidth: isSingleControl ? "400px" : "800px" }}>
                {(() => {
                  // Map selected IDs to actual file objects
                  const selectedFilesData = selectedFiles.map(getFileById).filter(Boolean);
                  // Determine if ribo or rna files are selected 
                  const hasRibo = selectedFilesData.some(f => 
                    f.file_type === "riboseq" || f.file_name?.toLowerCase().includes("ribo")
                  );
                  const hasRna = selectedFilesData.some(f => 
                    f.file_type === "rnaseq" || f.file_name?.toLowerCase().includes("rna")
                  );
                  
                  // If neither detected, fallback to showing both
                  const showBothFallback = !hasRibo && !hasRna;
                  const showRiboButtons = hasRibo || showBothFallback;
                  const showRnaButtons = hasRna || showBothFallback;

                  return displayGroups
                    .filter((group) => {
                      // timepoint must be visible
                      const isVisibleTimepoint = group.timepointIndex <= currentMaxVisible;
                      
                      // Hide "treat" groups if analysis is "single_control"
                      const isExcludedTreat = group.condType === "treat" && settings.analysisType === "single_control";
                      
                      // Filter by the detected sequence type of the selected files
                      const isMatchingSeqType = 
                        (group.seqType === "ribo" && showRiboButtons) || 
                        (group.seqType === "rna" && showRnaButtons);
                      
                      // Keep the group if it's a valid timepoint AND it is NOT excluded AND matches seqType
                      return isVisibleTimepoint && !isExcludedTreat && isMatchingSeqType;
                    })
                    .map((group) => (
                      <div className={isSingleControl ? "col-6" : "col-3"} key={group.id}>
                        <Button
                          size="sm"
                          style={{
                            backgroundColor: group.color,
                            borderColor: group.color,
                            color: "black",
                          }}
                          onClick={() => {
                            onAddFilesToTimepoint(group.timepointIndex, group.seqType, group.condType, selectedFiles);
                            setSelectedFiles([]);
                          }}
                        >
                          {`${group.condType} time_${group.timepointIndex} ${group.seqType}-seq`}
                        </Button>
                      </div>
                    ));
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Control/Treatment Name Inputs */}
        <Row className="mb-2">
          <Col md={shouldShowTreatment() ? 6 : 12}>
            <Input
              type="text"
              value={settings.customControlName || "Control"}
              onChange={(e) =>
                onUpdateSetting?.("customControlName", e.target.value)
              }
              placeholder="Control"
              style={{
                fontSize: "14px",
                textAlign: "center",
                fontWeight: "600",
                borderRadius: "8px",
                backgroundColor: "#e3f2fd",
                border: "2px solid #1976d2",
                color: "#1976d2",
              }}
            />
          </Col>
          {shouldShowTreatment() && (
            <Col md={6}>
              <Input
                type="text"
                value={settings.customTreatmentName || "Treatment"}
                onChange={(e) =>
                  onUpdateSetting?.("customTreatmentName", e.target.value)
                }
                placeholder="Treatment"
                style={{
                  fontSize: "14px",
                  textAlign: "center",
                  fontWeight: "600",
                  borderRadius: "8px",
                  backgroundColor: "#fce4ec",
                  border: "2px solid #d32f2f",
                  color: "#d32f2f",
                }}
              />
            </Col>
          )}
        </Row>

        {/* Timepoint Grid */}
        {timepoints
          .slice(0, currentMaxVisible + 1)
          .map((timepoint, timepointIndex) => (
            <div
              key={timepointIndex}
              className="mb-1"
              data-timepoint={timepointIndex}
            >
              {/* Timepoint Name Input */}
              <div className="mb-1">
                <Input
                  type="text"
                  value={timepoint.groupName}
                  maxLength={13}
                  onChange={(e) =>
                    onUpdateTimepointName(timepointIndex, e.target.value)
                  }
                  placeholder={`Timepoint ${timepointIndex + 1}`}
                  style={{
                    fontSize: "16px",
                    textAlign: "center",
                    fontWeight: "600",
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                    border: "2px solid #6c757d",
                    color: "#495057",
                    padding: "3px 16px",
                  }}
                />
              </div>

              <Row>
                {/* Control Column */}
                <Col md={shouldShowTreatment() ? 6 : 12}>
                  <Row>
                    {/* Control Ribo-Seq */}
                    {shouldShowSeqType("ribo") && (
                      <Col md={shouldShowSeqType("rna") ? 6 : 12}>
                        <TimepointCard
                          seqType="ribo"
                          condType="control"
                          files={timepoint.controlRiboFiles}
                          timepointIndex={timepointIndex}
                          dragOverTarget={dragOverTarget}
                          onDragEnter={(e) =>
                            handleDragEnter(
                              e,
                              timepointIndex,
                              "ribo",
                              "control"
                            )
                          }
                          onDragOver={(e) =>
                            handleDragOver(e, timepointIndex, "ribo", "control")
                          }
                          onDragLeave={handleDragLeave}
                          onDrop={(e) =>
                            handleDrop(e, timepointIndex, "ribo", "control")
                          }
                          onClearTimepoint={handleClearTimepointSection}
                          renderFileItem={renderFileItem}
                          getBorderColor={getBorderColor}
                          getBackgroundColor={getBackgroundColor}
                        />
                      </Col>
                    )}

                    {/* Control RNA-Seq */}
                    {shouldShowSeqType("rna") && (
                      <Col md={shouldShowSeqType("ribo") ? 6 : 12}>
                        <TimepointCard
                          seqType="rna"
                          condType="control"
                          files={timepoint.controlRnaFiles}
                          timepointIndex={timepointIndex}
                          dragOverTarget={dragOverTarget}
                          onDragEnter={(e) =>
                            handleDragEnter(e, timepointIndex, "rna", "control")
                          }
                          onDragOver={(e) =>
                            handleDragOver(e, timepointIndex, "rna", "control")
                          }
                          onDragLeave={handleDragLeave}
                          onDrop={(e) =>
                            handleDrop(e, timepointIndex, "rna", "control")
                          }
                          onClearTimepoint={handleClearTimepointSection}
                          renderFileItem={renderFileItem}
                          getBorderColor={getBorderColor}
                          getBackgroundColor={getBackgroundColor}
                        />
                      </Col>
                    )}
                  </Row>
                </Col>

                {/* Treatment Column */}
                {shouldShowTreatment() && (
                  <Col md={6}>
                    <Row>
                      {/* Treatment Ribo-Seq */}
                      {shouldShowSeqType("ribo") && (
                        <Col md={shouldShowSeqType("rna") ? 6 : 12}>
                          <TimepointCard
                            seqType="ribo"
                            condType="treat"
                            files={timepoint.treatRiboFiles}
                            timepointIndex={timepointIndex}
                            dragOverTarget={dragOverTarget}
                            onDragEnter={(e) =>
                              handleDragEnter(
                                e,
                                timepointIndex,
                                "ribo",
                                "treat"
                              )
                            }
                            onDragOver={(e) =>
                              handleDragOver(e, timepointIndex, "ribo", "treat")
                            }
                            onDragLeave={handleDragLeave}
                            onDrop={(e) =>
                              handleDrop(e, timepointIndex, "ribo", "treat")
                            }
                            onClearTimepoint={handleClearTimepointSection}
                            renderFileItem={renderFileItem}
                            getBorderColor={getBorderColor}
                            getBackgroundColor={getBackgroundColor}
                          />
                        </Col>
                      )}

                      {/* Treatment RNA-Seq */}
                      {shouldShowSeqType("rna") && (
                        <Col md={shouldShowSeqType("ribo") ? 6 : 12}>
                          <TimepointCard
                            seqType="rna"
                            condType="treat"
                            files={timepoint.treatRnaFiles}
                            timepointIndex={timepointIndex}
                            dragOverTarget={dragOverTarget}
                            onDragEnter={(e) =>
                              handleDragEnter(e, timepointIndex, "rna", "treat")
                            }
                            onDragOver={(e) =>
                              handleDragOver(e, timepointIndex, "rna", "treat")
                            }
                            onDragLeave={handleDragLeave}
                            onDrop={(e) =>
                              handleDrop(e, timepointIndex, "rna", "treat")
                            }
                            onClearTimepoint={handleClearTimepointSection}
                            renderFileItem={renderFileItem}
                            getBorderColor={getBorderColor}
                            getBackgroundColor={getBackgroundColor}
                          />
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </div>
          ))}

        {timepoints.length === 0 && (
          <div className="text-center py-4">
            <div className="text-muted">
              <i
                className="ri-time-line"
                style={{
                  fontSize: "48px",
                  display: "block",
                  marginBottom: "16px",
                }}
              ></i>
              <h6>No timepoints added yet</h6>
              <p>Click "Add Timepoint" to get started</p>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default TimeSeriesGroupsManager;
